import { useCallback } from 'react';
import type { ObjectDefinitionSpec } from 'shared-types';
import { useObjectAssetUploader } from '../objectEditor/useUploadObjectAsset';
import { SketchObjectCategory } from '../../potree/sketchObjects/types';
import { ObjectDefinitionAssetKind, useObjectDefinitionCreateMutation, useObjectDefinitionSaveDraftMutation, useObjectDefinitionUpdateDetailsMutation, type ObjectDefinitionFieldsFragment } from '../../types/graphqlTypes';
import { buildObjectFile, fromBase64, importCategory, MAX_OBJECT_FILE_BYTES, objectFileName, parseObjectFile, toBase64, type ObjectFile, type ObjectFileParseError } from './objectFile';

/** What export needs of a library card: its drawing, and the definition behind it when it is the organisation's own. */
export interface ExportableObject {
  name: string;
  categoryId: string;
  categoryLabel: string | null;
  spec: ObjectDefinitionSpec;
  definition: ObjectDefinitionFieldsFragment | null;
}

export type ObjectImportError = ObjectFileParseError | 'TOO_LARGE' | 'FAILED';

export interface ObjectImportResult {
  imported: Array<{ file: string; definitionId: string }>;
  failed: Array<{ file: string; error: ObjectImportError }>;
}

function download(name: string, content: string): void {
  const url = URL.createObjectURL(new Blob([content], { type: 'application/json' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Export a library object to a `.ptobj` file (its 3D model embedded), and
 * import such files as new drafts of this organisation.
 */
export function useObjectFiles(organisationId: string, categories: ReadonlyArray<{ id: string; label: string }>) {
  const [createDefinition] = useObjectDefinitionCreateMutation();
  const [saveDraft] = useObjectDefinitionSaveDraftMutation();
  const [updateDetails] = useObjectDefinitionUpdateDetailsMutation();
  const uploadAsset = useObjectAssetUploader(organisationId);

  const exportObject = useCallback(async (object: ExportableObject): Promise<void> => {
    const { definition, spec } = object;
    let model: ObjectFile['model'] = null;
    const modelAsset = spec.model3d && definition?.assets.find((a) => a.id === spec.model3d?.assetId && a.kind === ObjectDefinitionAssetKind.ModelGlb);
    if (modelAsset?.url) {
      const response = await fetch(modelAsset.url);
      if (!response.ok) throw new Error(`model download failed (${response.status})`);
      model = { fileName: modelAsset.originalName, data: toBase64(new Uint8Array(await response.arrayBuffer())) };
    }
    const file = buildObjectFile({
      name: object.name,
      description: definition?.description ?? null,
      categoryId: object.categoryId,
      categoryLabel: object.categoryLabel,
      tags: definition ? [...definition.tags] : [],
      smart: definition?.smart ?? false,
      // A model that can't come along isn't referenced either.
      spec: model || !spec.model3d ? spec : { ...spec, model3d: null },
      model,
    });
    download(objectFileName(object.name), JSON.stringify(file));
  }, []);

  /** One file → one new draft: created without the model, the model uploaded to it, then the spec with the new asset. */
  const importOne = useCallback(
    async (file: File): Promise<{ definitionId: string } | { error: ObjectImportError }> => {
      if (file.size > MAX_OBJECT_FILE_BYTES) return { error: 'TOO_LARGE' };
      const parsed = parseObjectFile(await file.text());
      if (!parsed.ok) return { error: parsed.error };
      const { name, spec, model, tags, description, smart } = parsed.file;
      const categoryId = importCategory(parsed.file, categories, SketchObjectCategory.FURNITURE);
      try {
        const created = await createDefinition({ variables: { organisationId, name, categoryId, spec: { ...spec, model3d: null }, smart } });
        const definitionId = created.data?.objectDefinitionCreate.id;
        if (!definitionId) return { error: 'FAILED' };
        if (model && spec.model3d) {
          const asset = await uploadAsset(definitionId, new Blob([fromBase64(model.data)], { type: 'model/gltf-binary' }), model.fileName, ObjectDefinitionAssetKind.ModelGlb, 'glb');
          await saveDraft({ variables: { organisationId, definitionId, spec: { ...spec, model3d: { ...spec.model3d, assetId: asset.id } } } });
        }
        if (tags.length > 0 || description) await updateDetails({ variables: { organisationId, definitionId, name, description: description ?? '', categoryId, tags } });
        return { definitionId };
      } catch {
        return { error: 'FAILED' };
      }
    },
    [categories, createDefinition, organisationId, uploadAsset, saveDraft, updateDetails],
  );

  const importFiles = useCallback(
    async (files: readonly File[]): Promise<ObjectImportResult> => {
      const result: ObjectImportResult = { imported: [], failed: [] };
      for (const file of files) {
        const outcome = await importOne(file);
        if ('definitionId' in outcome) result.imported.push({ file: file.name, definitionId: outcome.definitionId });
        else result.failed.push({ file: file.name, error: outcome.error });
      }
      return result;
    },
    [importOne],
  );

  return { exportObject, importFiles };
}
